import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, PackageCheck } from "lucide-react";
import axios from "axios";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("confirming");
  const { clearCart } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current < 12) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen flex items-center justify-center px-4 pt-16"
    >
      <motion.div
        className="max-w-md w-full border border-white/10 bg-panel p-12 text-center relative overflow-hidden"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="scanlines" />
        {status === "confirming" && (
          <>
            <Loader2 size={44} className="mx-auto mb-6 text-cyber animate-spin" />
            <h1 className="font-display text-2xl font-bold uppercase tracking-tight mb-3">
              Confirming payment
            </h1>
            <p className="text-sm text-slate-400" data-testid="payment-confirming-text">
              Securing your order with our payment vault…
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={44} className="mx-auto mb-6 text-magma" />
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-cyber mb-3">
              Order confirmed
            </p>
            <h1 className="font-display text-2xl font-bold uppercase tracking-tight mb-3" data-testid="payment-confirmed-heading">
              Your companion is being prepared
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed mb-8">
              White-glove delivery in an unmarked crate within 4–6 weeks. Your
              billing statement shows a neutral descriptor.
            </p>
            <div className="flex items-center justify-center gap-2 text-slate-500 mb-8">
              <PackageCheck size={14} className="text-cyber" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em]">
                Ref {sessionId.slice(-10).toUpperCase()}
              </span>
            </div>
            <Link
              to="/"
              data-testid="back-to-store-btn"
              className="inline-block bg-magma text-white font-display font-bold uppercase tracking-widest text-sm px-8 py-4 glow-magenta hover:bg-[#ff4d87] transition-colors"
            >
              Back to the Collection
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <Loader2 size={44} className="mx-auto mb-6 text-slate-500" />
            <h1 className="font-display text-2xl font-bold uppercase tracking-tight mb-3" data-testid="payment-pending-heading">
              Still processing
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed mb-8">
              Your payment is being confirmed by Stripe. If you completed
              checkout, your order is safe — check back shortly.
            </p>
            <Link
              to="/"
              className="inline-block border border-white/15 text-slate-300 font-display font-bold uppercase tracking-widest text-sm px-8 py-4 hover:border-magma hover:text-magma transition-colors"
            >
              Back to the Collection
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
